import { queryOptions } from "@tanstack/react-query";
import { getSupabase } from "@/shared/api/supabase";
import { parseRow, parseRows } from "@/shared/lib/parse";
import { CategorySchema, type Category, type CategoryNode } from "@/shared/lib/db-schema";
import { keys } from "@/shared/lib/query-keys";
import { STALE_TIMES } from "@/shared/config/cache";
import { buildCategoryTree } from "../lib/tree";

const CATEGORY_COLS =
  "id,parent_id,slug,name,description,icon,image_url,sort_order,active,created_at,updated_at,deleted_at";

async function fetchCategories(): Promise<ReadonlyArray<Category>> {
  const { data, error } = await getSupabase()
    .from("categories")
    .select(CATEGORY_COLS)
    .eq("active", true)
    .is("deleted_at", null)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return parseRows(CategorySchema, data ?? [], "Category");
}

async function fetchCategoryTree(): Promise<ReadonlyArray<CategoryNode>> {
  return buildCategoryTree(await fetchCategories());
}

/** Active category tree (catalog filters). */
export function categoryTreeOptions() {
  return queryOptions({
    queryKey: keys.category.tree(),
    queryFn: fetchCategoryTree,
    staleTime: STALE_TIMES.CATEGORIES,
  });
}

/** Single category by id (admin / deep links). */
export function categoryByIdOptions(id: string) {
  return queryOptions({
    queryKey: [...keys.category.all, "by-id", id] as const,
    queryFn: async (): Promise<Category | null> => {
      const { data, error } = await getSupabase()
        .from("categories").select(CATEGORY_COLS).eq("id", id).is("deleted_at", null).maybeSingle();
      if (error) throw error;
      return data ? parseRow(CategorySchema, data, "Category") : null;
    },
  });
}
